import styled from 'styled-components';
import React from 'react';
import {CarSelectionSection} from './components/car-selection-section';
import {InputSection} from './components/input-section';
import {OutputSection} from './components/output-section';
import {useRangeCalculationForm} from './use-range-calculation-form';
import {
  ConfigureButtonWrapper,
  FaqListContextProvider,
  FaqSection,
  getConfigureButtonConfiguration,
  getDisclaimerReferenceProps,
  GridAreaConfigure,
  GridAreaDisclaimers,
  GridAreaInputs,
  GridAreaOutputs,
  Header,
  resultBarInViewProps,
  TrackingEvent,
  useComponent,
  useSharedEmbeddedFeatureAppData,
  useTrackingContextSetter,
  useTrackingFunctions,
  SimulatorLayout,
  SimulatorHeader,
  useGetAppDataContentFlag,
  useRangeCalculation,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useTenantConfiguration} from './use-tenant-configuration';
import {useTranslation} from './use-translation';
import {RangeSimulatorTrackingCtx} from './range-tracking';
import {useInView} from 'react-intersection-observer';
import {
  OutsideTemperatures,
  RangeTrimModel,
  SimulatorFeatureAppName,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {useTrims} from './use-trims';

export function App(): JSX.Element {
  const tenantConfiguration = useTenantConfiguration();
  const translations = useTranslation();
  const isContentLayer = useGetAppDataContentFlag('isContentLayer');

  const setTrackingData = useTrackingContextSetter<RangeSimulatorTrackingCtx>();

  const resultDisclaimer = getDisclaimerReferenceProps(
    translations.resultSection.disclaimerText,
    translations.global.disclaimerReferenceLabel,
    translations.global.educationalInfoLayer.closeButtonLabel
  );

  const isCommercial = React.useMemo(
    () => Boolean(tenantConfiguration.global?.commercial),
    [tenantConfiguration]
  );

  const tracking = useTrackingFunctions();

  React.useEffect(() => {
    tracking[TrackingEvent.FeatureAppLoaded]?.({});
  }, [tracking]);

  const {formFields, formState, selectedModel, selectedTrim, selectedRim} =
    useRangeCalculationForm();

  React.useMemo(() => {
    // useMemo instead of useEffect to be synchronous and faster than the load
    // event. This is ok for this use-case where we only write data into a ref.
    setTrackingData({
      tenantConfiguration,
      translations,
      selectedTrim,
      selectedModel,
      selectedRim,
      formState,
    });
  }, [
    setTrackingData,
    tenantConfiguration,
    translations,
    selectedTrim,
    selectedModel,
    selectedRim,
    formState,
  ]);

  useSharedEmbeddedFeatureAppData<RangeTrimModel>(
    useTrims(),
    formFields.modelTypeId.onChange
  );

  const rangeKm = useRangeCalculation(selectedModel, {
    rim: formState.rim,
    payloadPeople: formState.payloadPeople,
    payloadWeight: formState.payloadWeight,
    drivingProfile: formState.drivingProfile,
    outsideTemperature: formState.outsideTemperature as OutsideTemperatures,
    airConditioning: formState.airConditioning,
    fasterHighway: formState.fasterHighway,
  });
  const Disclaimer = useComponent('Disclaimer');

  const {ref, inView} = useInView(resultBarInViewProps);

  const configureButtonConfiguration =
    getConfigureButtonConfiguration(selectedTrim);

  return (
    <FaqListContextProvider>
      <AppLayout>
        <SimulatorLayout>
          <SimulatorHeader isContentLayer={isContentLayer}>
            <Header
              heading={translations.header.heading}
              showConfigureButton={
                tenantConfiguration.featureFlags?.showConfigureButton
              }
              configureButtonTranslations={translations.global.configureButton}
              configureButtonConfiguration={configureButtonConfiguration}
              featureApp={SimulatorFeatureAppName.Range}
            />
          </SimulatorHeader>
          <SimulatorContentLayout isContentLayer={isContentLayer}>
            <GridAreaCarSelection>
              <CarSelectionSection
                selectedModel={selectedModel}
                selectedTrim={selectedTrim}
                modelTypeField={formFields.modelTypeId}
                modelEngineField={formFields.modelEngineId}
              />
            </GridAreaCarSelection>
            <GridAreaInputs innerRef={ref}>
              <InputSection
                isCommercial={isCommercial}
                drivingProfileField={formFields.drivingProfile}
                outsideTemperatureField={formFields.outsideTemperature}
                payloadWeightField={formFields.payloadWeight}
                payloadPeopleField={formFields.payloadPeople}
                fasterHighwayField={formFields.fasterHighway}
                airConditioningField={formFields.airConditioning}
                payloadUI={selectedModel.simulationConstants.payloadUi}
                selectedModel={selectedModel}
                tireTypeField={formFields.tireType}
                rimField={formFields.rim}
              />
            </GridAreaInputs>
            <GridAreaOutputs>
              <OutputSection
                disclaimer={resultDisclaimer}
                rangeKm={rangeKm}
                inView={inView}
              />
            </GridAreaOutputs>
            <GridAreaConfigure>
              <ConfigureButtonWrapper
                showButton={
                  tenantConfiguration.featureFlags?.showConfigureButton
                }
                configuration={configureButtonConfiguration}
                translations={translations.global.configureButton}
              />
            </GridAreaConfigure>
            {resultDisclaimer && (
              <GridAreaDisclaimers>
                <Disclaimer
                  reference={resultDisclaimer.reference}
                  text={resultDisclaimer.text}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- this is guaranteed to exist for module references. We decided against a complex type that would prevent the need of the assertion.
                  id={resultDisclaimer.disclaimerTextId!}
                />
              </GridAreaDisclaimers>
            )}
          </SimulatorContentLayout>
        </SimulatorLayout>

        {!tenantConfiguration.featureFlags?.hideFaq && (
          <FaqSection translations={translations.faqSection} />
        )}
      </AppLayout>
    </FaqListContextProvider>
  );
}

const AppLayout = styled.div`
  > * + * {
    margin-block-start: ${(props) =>
      props.theme.brand.spacing.appLayout?.blockGap ?? '0'};
  }
`;

const SimulatorContentLayout = styled.div<{isContentLayer?: boolean}>`
  display: grid;
  grid-template:
    'carsel'
    'inputs'
    'output'
    'configure'
    'resultdisclaimer' / 24fr;
  align-items: stretch;
  justify-items: stretch;
  max-width: 2560px;
  padding-inline-start: ${(props) =>
    props.theme.brand.spacing.oneColumnSimulatorContentLayout?.inlineStart ??
    '0'};
  padding-inline-end: ${(props) =>
    props.theme.brand.spacing.oneColumnSimulatorContentLayout?.inlineEnd ??
    '0'};

  @media (min-width: ${(props) =>
      props.theme.brand.spacing.customOneColumnBreakpoint ||
      props.theme.internal.Breakpoints.b960}px) {
    grid-template:
      ' carsel inputs '
      ' output inputs '
      ' . inputs '
      ' configure configure '
      ' resultdisclaimer resultdisclaimer ';
    grid-template-columns: ${(props) =>
        props.theme.brand.spacing.twoColumnSimulatorContentLayout
          ?.columnWidths?.[0] ?? '1fr'} ${(props) =>
        props.theme.brand.spacing.twoColumnSimulatorContentLayout
          ?.columnWidths?.[1] ?? '1fr'};
    padding-inline-start: ${(props) =>
      props.isContentLayer
        ? '0'
        : props.theme.brand.spacing.twoColumnSimulatorContentLayout
            ?.inlineStart ?? '0'};
    padding-inline-end: ${(props) =>
      props.isContentLayer
        ? '0'
        : props.theme.brand.spacing.twoColumnSimulatorContentLayout
            ?.inlineEnd ?? '0'};
    column-gap: ${(props) =>
      props.theme.brand.spacing.twoColumnSimulatorContentLayout?.inlineGap ??
      '0'};
  }
`;

const GridAreaCarSelection = styled.div`
  grid-area: carsel;
  height: 100%;
  padding-block-start: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.blockStart ?? '0'};
  padding-block-end: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.blockEnd ?? '0'};
  padding-inline-start: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.inlineStart ?? '0'};
  padding-inline-end: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.inlineEnd ?? '0'};
`;
